"use client";

import { useMemo, useRef, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  type BarStyle,
  BlackoutPage,
} from "@/components/tools/poetry/blackout-page";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import {
  indexRange,
  keptLines,
  layoutWords,
  MAX_WORDS,
  PAGE_COLUMNS,
  selectInOrder,
  type Token,
  tokenize,
} from "@/lib/tool-kit/poetry/blackout";

/** English strings. A translated content file overrides them through `ui`. */
export const BLACKOUT_POETRY_MAKER_STRINGS = {
  textLabel: "The page — paste text you may use",
  textPlaceholder: "Paste a page of text here…",
  textHint:
    "Use text that is in the public domain or that you wrote yourself. Editing the text clears your choices.",
  loadSample: "Load a public-domain sample",
  sampleText:
    "It is a truth universally acknowledged, that a single man in possession of a good fortune must be in want of a wife.\n\nHowever little known the feelings or views of such a man may be on his first entering a neighbourhood, this truth is so well fixed in the minds of the surrounding families, that he is considered as the rightful property of some one or other of their daughters.\n\n“My dear Mr. Bennet,” said his lady to him one day, “have you heard that Netherfield Park is let at last?”",
  sampleKeep:
    "truth universally acknowledged single man want wife feelings fixed minds rightful property daughters",
  sampleCredit: "Page: Jane Austen, Pride and Prejudice (1813), chapter 1",
  chooseHeading: "Choose the words to keep",
  chooseHint:
    "Click a word to keep it. Shift-click a second word to keep everything between. Punctuation stays with its word.",
  wordsKept: "{kept} of {total} words kept",
  truncated:
    "Only the first {max} words are used — {total} were pasted. Work a page at a time.",
  emptyPrompt: "Paste some text, or load the sample, to start choosing words.",
  noneKept: "Nothing is kept yet, so the whole page is blacked out.",
  keepAll: "Keep every word",
  keepNone: "Black out every word",
  invert: "Swap kept and blacked out",
  previewHeading: "Your blackout poem",
  barStyleLabel: "Bars",
  barStyles: {
    solid: "Solid black",
    grey: "Dark grey (uses less ink)",
    outline: "Outlines only (colour in by hand)",
  },
  creditLabel: "Source line under the page (optional)",
  creditHint: "Up to 80 characters, printed beneath the page.",
  creditPlaceholder: "From Title by Author (year)",
  pageLabel: "Blackout poem page. Kept words: {poem}",
  pageLabelEmpty: "Blackout poem page with every word blacked out",
  poemHeading: "The poem on its own",
  copyPoem: "Copy poem text",
  copied: "Copied!",
  print: "Print the page",
  downloadPng: "Download PNG",
  downloadSvg: "Download SVG",
  pngFailed:
    "The PNG could not be made in this browser. Download the SVG, or print the page and choose Save as PDF.",
  pngName: "blackout-poem.png",
  svgName: "blackout-poem.svg",
} as const;

const ID = "blackout";
const NO_SELECTION: ReadonlySet<number> = new Set();
const BAR_STYLES: readonly BarStyle[] = ["solid", "grey", "outline"];
const KEPT_WORD =
  "mr-1 rounded bg-primary px-1 py-0.5 text-primary-foreground text-sm";
const OPEN_WORD =
  "mr-1 rounded px-1 py-0.5 text-foreground text-sm hover:bg-muted";

function byParagraph(tokens: readonly Token[]): Token[][] {
  const groups: Token[][] = [];
  for (const token of tokens) {
    const group = groups.at(-1);
    if (group && group[0].paragraph === token.paragraph) {
      group.push(token);
    } else {
      groups.push([token]);
    }
  }
  return groups;
}

export default function BlackoutPoetryMakerWidget() {
  const strings = useToolUi(BLACKOUT_POETRY_MAKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [kept, setKept] = useState<ReadonlySet<number>>(NO_SELECTION);
  const [anchor, setAnchor] = useState<number | null>(null);
  const [barStyle, setBarStyle] = useState<BarStyle>("solid");
  const [credit, setCredit] = useState("");
  const [failed, setFailed] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const { tokens, truncated, total } = useMemo(() => tokenize(text), [text]);
  const layout = useMemo(() => layoutWords(tokens), [tokens]);
  const paragraphs = useMemo(() => byParagraph(tokens), [tokens]);
  const lines = keptLines(tokens, layout.items, kept);
  const shownCredit = credit.trim().slice(0, 80);
  const label =
    lines.length > 0
      ? t(strings.pageLabel, { poem: lines.join(" / ") })
      : strings.pageLabelEmpty;

  function editText(value: string) {
    setText(value);
    setKept(NO_SELECTION);
    setAnchor(null);
  }

  function loadSample() {
    const sample = tokenize(strings.sampleText);
    setText(strings.sampleText);
    setKept(selectInOrder(sample.tokens, strings.sampleKeep.split(" ")));
    setCredit(strings.sampleCredit);
    setAnchor(null);
  }

  function pick(index: number, range: boolean) {
    const next = new Set(kept);
    if (range && anchor !== null) {
      for (const i of indexRange(anchor, index)) {
        next.add(i);
      }
    } else if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setKept(next);
    setAnchor(index);
  }

  function invert() {
    setKept(
      new Set(tokens.map((token) => token.index).filter((i) => !kept.has(i)))
    );
  }

  async function savePng() {
    setFailed(false);
    if (!svgRef.current) {
      return;
    }
    try {
      await downloadPng(strings.pngName, svgRef.current, { scale: 2 });
    } catch {
      setFailed(true);
    }
  }

  function saveSvg() {
    if (svgRef.current) {
      downloadSvg(strings.svgName, svgRef.current);
    }
  }

  return (
    <>
      <style>{"@media print { header, footer, nav { display: none } }"}</style>

      <div className="space-y-3 print:hidden">
        <TextAreaField
          hint={strings.textHint}
          id={`${ID}-text`}
          label={strings.textLabel}
          onChange={editText}
          placeholder={strings.textPlaceholder}
          prose
          value={text}
        />
        <button className={BUTTON_SECONDARY} onClick={loadSample} type="button">
          {strings.loadSample}
        </button>
      </div>

      {tokens.length === 0 ? (
        <div className="print:hidden">
          <Notice>{strings.emptyPrompt}</Notice>
        </div>
      ) : (
        <>
          <section className="space-y-3 print:hidden">
            <h2 className={WIDGET_H2}>{strings.chooseHeading}</h2>
            <p className={HINT}>{strings.chooseHint}</p>
            {truncated ? (
              <Notice>
                {t(strings.truncated, {
                  max: fmt(MAX_WORDS),
                  total: fmt(total),
                })}
              </Notice>
            ) : null}
            <div className="space-y-3 rounded-lg border border-border bg-card p-4">
              {paragraphs.map((group) => (
                <p className="leading-9" key={group[0].index}>
                  {group.map((token) => (
                    <button
                      aria-pressed={kept.has(token.index)}
                      className={kept.has(token.index) ? KEPT_WORD : OPEN_WORD}
                      key={token.index}
                      onClick={(event) => pick(token.index, event.shiftKey)}
                      type="button"
                    >
                      {token.text}
                    </button>
                  ))}
                </p>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  setKept(new Set(tokens.map((token) => token.index)))
                }
                type="button"
              >
                {strings.keepAll}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => setKept(NO_SELECTION)}
                type="button"
              >
                {strings.keepNone}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={invert}
                type="button"
              >
                {strings.invert}
              </button>
              <p aria-live="polite" className={HINT}>
                {t(strings.wordsKept, {
                  kept: fmt(kept.size),
                  total: fmt(tokens.length),
                })}
              </p>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className={`${WIDGET_H2} print:hidden`}>
              {strings.previewHeading}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 print:hidden">
              <SelectField
                id={`${ID}-bars`}
                label={strings.barStyleLabel}
                onChange={setBarStyle}
                options={BAR_STYLES.map((value) => ({
                  value,
                  label: strings.barStyles[value],
                }))}
                value={barStyle}
              />
              <TextField
                hint={strings.creditHint}
                id={`${ID}-credit`}
                label={strings.creditLabel}
                onChange={setCredit}
                placeholder={strings.creditPlaceholder}
                value={credit}
              />
            </div>
            <div className="overflow-hidden rounded-lg border border-border print:rounded-none print:border-0">
              <BlackoutPage
                barStyle={barStyle}
                columns={PAGE_COLUMNS}
                credit={shownCredit}
                items={layout.items}
                kept={kept}
                label={label}
                rows={layout.rows}
                svgRef={svgRef}
                tokens={tokens}
              />
            </div>
            <div className="flex flex-wrap gap-3 print:hidden">
              <button
                className={BUTTON_PRIMARY}
                onClick={() => window.print()}
                type="button"
              >
                {strings.print}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={savePng}
                type="button"
              >
                {strings.downloadPng}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={saveSvg}
                type="button"
              >
                {strings.downloadSvg}
              </button>
            </div>
            {failed ? <ErrorNotice>{strings.pngFailed}</ErrorNotice> : null}
            <div className="space-y-2 print:hidden">
              <h3 className="font-medium text-foreground text-sm">
                {strings.poemHeading}
              </h3>
              {lines.length > 0 ? (
                <p className="whitespace-pre-line rounded-lg border border-border bg-card p-4 text-foreground text-sm">
                  {lines.join("\n")}
                </p>
              ) : (
                <Notice>{strings.noneKept}</Notice>
              )}
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyPoem}
                text={lines.join("\n")}
              />
            </div>
          </section>
        </>
      )}
    </>
  );
}
